import React, { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { F } from "../fonts";
import { useDesigns } from "../api";

const C = {
  vanilla: "#F8F4EE",
  umber:   "#443A35",
};

const POSTER_CATEGORIES = ["Posters", "Graphic Design"];

const PosterGrid = ({ images, onSelect }) => (
  <Box sx={{
    display: "grid",
    gridTemplateColumns: "repeat(5, 1fr)",
    gap: "10px",
  }}>
    {images.map((img, i) => (
      <Box
        key={i}
        onClick={() => onSelect(img)}
        sx={{
          aspectRatio: "2 / 3",
          backgroundColor: "rgba(248,244,238,0.08)",
          borderRadius: "4px",
          overflow: "hidden",
          cursor: "pointer",
          position: "relative",
          transition: "transform 0.3s cubic-bezier(0.4,0,0.2,1)",
          "&:hover": { transform: "scale(1.025)" },
          "&:hover .po": { opacity: 1 },
        }}
      >
        <Box
          component="img"
          src={img}
          alt=""
          sx={{
            width: "100%", height: "100%",
            objectFit: "cover",
            display: "block",
          }}
        />
        <Box className="po" sx={{
          position: "absolute", inset: 0,
          backgroundColor: "rgba(248,244,238,0.05)",
          opacity: 0,
          transition: "opacity 0.3s ease",
        }} />
      </Box>
    ))}
  </Box>
);

const PostersPage = () => {
  const navigate = useNavigate();
  const [selected, setSelected] = useState(null);
  const { designs } = useDesigns();
  const sections = designs
    .filter((d) => POSTER_CATEGORIES.includes(d.category))
    .map((d) => ({ id: d._id, label: d.title.toUpperCase(), images: d.gallery || [] }));

  useEffect(() => {
    if (!selected) return;
    const onKeyDown = (e) => { if (e.key === "Escape") setSelected(null); };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [selected]);

  return (
    <Box sx={{ backgroundColor: C.umber, minHeight: "100vh", color: C.vanilla }}>

      {/* ── Top bar ── */}
      <Box sx={{
        px: "40px", py: "22px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        borderBottom: "1px solid rgba(248,244,238,0.08)",
        position: "sticky", top: 0, zIndex: 100,
        backgroundColor: "rgba(68,58,53,0.92)",
        backdropFilter: "blur(12px)",
      }}>
        <Box
          onClick={() => navigate("/")}
          sx={{
            display: "flex", alignItems: "center", gap: 1.5,
            cursor: "pointer", color: "rgba(248,244,238,0.5)",
            transition: "color 0.2s ease",
            "&:hover": { color: C.vanilla },
          }}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="19" y1="12" x2="5" y2="12"/>
            <polyline points="12 19 5 12 12 5"/>
          </svg>
          <Typography sx={{ fontSize: "12px", letterSpacing: "2.5px", fontFamily: F.heading }}>BACK</Typography>
        </Box>
      </Box>

      {/* ── Page title ── */}
      <Box sx={{ px: "40px", pt: "70px", pb: "60px" }}>
        <Typography sx={{
          fontSize: "clamp(60px, 9vw, 110px)",
          fontFamily: F.heading,
          fontWeight: 700,
          letterSpacing: "5px",
          lineHeight: 0.95,
          color: C.vanilla,
        }}>
          GRAPHIC
        </Typography>
        <Typography sx={{
          fontSize: "clamp(60px, 9vw, 110px)",
          fontFamily: F.heading,
          fontWeight: 700,
          letterSpacing: "5px",
          lineHeight: 0.95,
          color: "rgba(248,244,238,0.12)",
        }}>
          DESIGN
        </Typography>
        <Typography sx={{
          mt: 4,
          fontSize: "14px",
          fontFamily: F.body,
          color: "rgba(248,244,238,0.38)",
          letterSpacing: "0.5px",
          maxWidth: "480px",
          lineHeight: 1.7,
        }}>
          A collection of posters, social media graphics, and album artwork — bold
          visual work exploring typography, colour, and composition.
        </Typography>
      </Box>

      {/* ── Sections ── */}
      <Box sx={{ px: "40px", pb: "100px" }}>
        {sections.map((section, idx) => (
          <Box key={section.id} sx={{ mb: idx < sections.length - 1 ? "80px" : 0 }}>

            {/* Section header */}
            <Box sx={{
              display: "flex",
              alignItems: "center",
              gap: "20px",
              mb: "24px",
              pb: "16px",
              borderBottom: "1px solid rgba(248,244,238,0.08)",
            }}>
              <Typography sx={{
                fontSize: "10px",
                fontFamily: "monospace",
                color: "rgba(248,244,238,0.25)",
                letterSpacing: "1px",
              }}>
                {String(idx + 1).padStart(2, "0")}
              </Typography>
              <Typography sx={{
                fontSize: "12px",
                letterSpacing: "4px",
                fontFamily: F.heading,
                fontWeight: 600,
                color: C.vanilla,
              }}>
                {section.label}
              </Typography>
              <Box sx={{ flex: 1, height: "1px", backgroundColor: "rgba(248,244,238,0.06)" }} />
              <Typography sx={{
                fontSize: "11px",
                fontFamily: F.body,
                color: "rgba(248,244,238,0.2)",
                letterSpacing: "1px",
              }}>
                {section.images.length} works
              </Typography>
            </Box>

            <PosterGrid images={section.images} onSelect={setSelected} />
          </Box>
        ))}
      </Box>

      {/* ── Lightbox ── */}
      {selected && (
        <Box
          onClick={() => setSelected(null)}
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(68,58,53,0.75)",
            backdropFilter: "blur(16px)",
            cursor: "pointer",
          }}
        >
          <Box
            onClick={() => setSelected(null)}
            sx={{
              position: "absolute", top: "28px", right: "40px",
              width: "36px", height: "36px",
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer", opacity: 0.7,
              transition: "opacity 0.2s ease",
              "&:hover": { opacity: 1 },
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#F8F4EE" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </Box>
          <Box
            component="img"
            src={selected}
            alt=""
            onClick={(e) => e.stopPropagation()}
            sx={{
              maxWidth: "85vw",
              maxHeight: "85vh",
              objectFit: "contain",
              borderRadius: "4px",
              boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
              cursor: "default",
            }}
          />
        </Box>
      )}

    </Box>
  );
};

export default PostersPage;
